import React, { useState } from 'react'

export const ScheduleModal = ({ onSchedule, onClose, recipientCount = 0 }) => {
  const [scheduleDate, setScheduleDate] = useState('')
  const [scheduleTime, setScheduleTime] = useState('')
  const [showCustomTime, setShowCustomTime] = useState(false)
  const [customHour, setCustomHour] = useState('09')
  const [customMinute, setCustomMinute] = useState('00')
  
  const today = new Date()
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth())
  const [calendarYear, setCalendarYear] = useState(today.getFullYear())
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
  
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay()
    return day === 0 ? 6 : day - 1
  }
  
  const daysInMonth = getDaysInMonth(calendarMonth, calendarYear)
  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear)
  
  const isDateDisabled = (day) => {
    const date = new Date(calendarYear, calendarMonth, day)
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return date < todayStart
  }
  
  const isDateSelected = (day) => {
    if (!scheduleDate) return false
    const selected = new Date(scheduleDate)
    return selected.getDate() === day && selected.getMonth() === calendarMonth && selected.getFullYear() === calendarYear
  }
  
  const selectDate = (day) => {
    if (!isDateDisabled(day)) {
      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      setScheduleDate(dateStr)
    }
  }
  
  const prevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11)
      setCalendarYear(calendarYear - 1)
    } else {
      setCalendarMonth(calendarMonth - 1)
    }
  }
  
  const nextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0)
      setCalendarYear(calendarYear + 1)
    } else {
      setCalendarMonth(calendarMonth + 1)
    }
  }
  
  const canGoPrev = calendarYear > today.getFullYear() || (calendarYear === today.getFullYear() && calendarMonth > today.getMonth())

  // A time slot is in the past when the selected date is today
  const isTimePast = (time) => {
    if (!scheduleDate) return false
    const candidate = new Date(`${scheduleDate}T${time}`)
    return candidate.getTime() <= Date.now()
  }
  const timeValid = scheduleDate && scheduleTime && !isTimePast(scheduleTime)

  const handleSchedule = () => {
    if (timeValid) {
      // The composer persists these as scheduledDate + scheduledTime
      onSchedule({ date: scheduleDate, time: scheduleTime })
    }
  }
  
  return (
    <>
      <div 
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 200
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '28px',
        width: '380px',
        maxWidth: '90vw',
        zIndex: 201,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 6px 0' }}>
          Schedule campaign
        </h2>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px 0' }}>
          Choose when to send to {recipientCount.toLocaleString()} recipients
        </p>
        
        {/* Calendar */}
        <div style={{ marginBottom: '20px' }}>
          {/* Calendar Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
              {monthNames[calendarMonth]} {calendarYear}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={prevMonth}
                disabled={!canGoPrev}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  backgroundColor: canGoPrev ? '#f5f5f5' : 'transparent',
                  borderRadius: '8px',
                  cursor: canGoPrev ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: canGoPrev ? 1 : 0.3
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={nextMonth}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Day Names */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '8px' }}>
            {dayNames.map(day => (
              <div key={day} style={{ 
                textAlign: 'center', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#9ca3af',
                padding: '4px 0'
              }}>
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {/* Empty cells for days before first of month */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} style={{ height: '36px' }} />
            ))}
            
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const disabled = isDateDisabled(day)
              const selected = isDateSelected(day)
              const isToday = day === today.getDate() && calendarMonth === today.getMonth() && calendarYear === today.getFullYear()
              
              return (
                <button
                  key={day}
                  onClick={() => selectDate(day)}
                  disabled={disabled}
                  style={{
                    height: '36px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: selected ? '#29282D' : 'transparent',
                    color: selected ? 'white' : disabled ? '#d4d4d4' : '#1a1a1a',
                    fontSize: '13px',
                    fontWeight: isToday && !selected ? '600' : '400',
                    cursor: disabled ? 'default' : 'pointer',
                    position: 'relative',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled && !selected) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled && !selected) {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }
                  }}
                >
                  {day}
                  {isToday && !selected && (
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: '#007EA0'
                    }} />
                  )}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Time Picker */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '10px' }}>
            Time
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: showCustomTime ? '12px' : '0' }}>
            {['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'].map(time => {
              const past = isTimePast(time)
              const active = scheduleTime === time && !showCustomTime
              return (
                <button
                  key={time}
                  onClick={() => {
                    if (past) return
                    setScheduleTime(time)
                    setShowCustomTime(false)
                  }}
                  disabled={past}
                  style={{
                    padding: '10px 0',
                    backgroundColor: active ? '#29282D' : 'white',
                    color: active ? 'white' : past ? '#C7C7CC' : '#4B4B52',
                    border: `1px solid ${active ? '#29282D' : '#ECECF0'}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: past ? 'default' : 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {time}
                </button>
              )
            })}
            <button
              onClick={() => setShowCustomTime(!showCustomTime)}
              style={{
                padding: '10px 16px',
                backgroundColor: showCustomTime ? '#29282D' : 'white',
                color: showCustomTime ? 'white' : '#374151',
                border: `1px solid ${showCustomTime ? '#29282D' : '#e5e5e5'}`,
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Custom
            </button>
          </div>
          
          {/* Custom Time Input */}
          {showCustomTime && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '10px'
            }}>
              <select
                value={customHour}
                onChange={(e) => {
                  setCustomHour(e.target.value)
                  setScheduleTime(`${e.target.value}:${customMinute}`)
                }}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '70px'
                }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>:</span>
              <select
                value={customMinute}
                onChange={(e) => {
                  setCustomMinute(e.target.value)
                  setScheduleTime(`${customHour}:${e.target.value}`)
                }}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '70px'
                }}
              >
                {['00', '15', '30', '45'].map(min => (
                  <option key={min} value={min}>{min}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {/* Summary */}
        {scheduleDate && scheduleTime && (
          <div style={{
            padding: '14px 16px',
            backgroundColor: timeValid ? 'rgba(0, 126, 160, 0.08)' : '#FEF2F2',
            borderRadius: '10px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={timeValid ? '#007EA0' : '#B14438'} strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontSize: '14px', color: timeValid ? '#007EA0' : '#B14438', fontWeight: '500' }}>
              {timeValid
                ? `Sends ${new Date(scheduleDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at ${scheduleTime}`
                : 'That time has already passed, pick a later one'}
            </span>
          </div>
        )}
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '1px solid #e5e5e5',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={!timeValid}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: timeValid ? '#29282D' : '#e5e5e5',
              color: timeValid ? 'white' : '#9ca3af',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: timeValid ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            Schedule
          </button>
        </div>
      </div>
    </>
  )
}
